/**
 * ModelsTable — every model this team calls, aliased or not.
 *
 * One component for the models page and the dashboard band, so the two can't
 * drift into showing different columns for the same rows.
 *
 * Catalog models the team called WITHOUT creating an alias (TRA-886) render
 * as rows in this same table rather than a section of their own. They are
 * not a different kind of thing to the reader — "what am I calling, and how
 * much" is one question, and answering it in two tables is what let the
 * headline request count disagree with the list underneath it. What they
 * lack is a platform row: no display name, no created date, nowhere to link.
 *
 * Catalog rows take their status from the same gateway probes a team model
 * does — never a hardcoded `live`, which let this band show green while the
 * Uptime band on the same page reported the provider down. The health cron
 * probes catalog deployments gateway-wide, and the overview endpoint joins
 * those probes onto catalog rows too — so `unknown` here means the probe
 * genuinely hasn't landed (new deployment, degraded read), and it is
 * deliberately not styled as a problem.
 *
 * `health: undefined` on a row means the gateway call hasn't landed yet —
 * rendered as an em dash rather than a zero, because "we don't know" and
 * "no traffic" are different facts.
 */

import { Link } from 'react-router-dom'

import type { DashboardModelRow, PublicModelUsageRow } from '@/api/generated/model'
import { DefaultModelBadge } from '@/components/models/DefaultModelBadge'
import { formatCompact } from '@/components/models/home/contracts'
import { ServingBadge } from '@/components/models/ServingBadge'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export type ModelTableRow = DashboardModelRow & {
  /** Canonical gateway id (`<teamSlug>/<alias>`); set by the models page. */
  gateway_model_id?: string
  /** The team's default deployed model (TRA-1215); set by the models page. */
  is_default?: boolean
  /** Whether the latency-alerts job watches this model (TRA-1414); set by the models page. */
  latency_alerts_enabled?: boolean
}

/**
 * Per-model trace-capture toggles (TRA-1009). Passed only where the column
 * belongs: the models page for a writable team. The dashboard band and
 * restricted demo teams omit it and render no column.
 */
export interface TraceLoggingColumn {
  isEnabled: (gatewayModelId: string) => boolean
  onToggle: (gatewayModelId: string, enabled: boolean) => void
  /** True while a toggle is in flight; disables every switch. */
  pending: boolean
}

/**
 * Per-model latency-alert switches (TRA-1414). Passed only on the models
 * page for a writable team; the dashboard band renders no column. Catalog
 * rows have no platform row to carry the flag and show an em dash.
 */
export interface LatencyAlertsColumn {
  onToggle: (modelId: string, enabled: boolean) => void
  /** True while a flip is in flight; disables every switch. */
  pending: boolean
}

function formatRelative(iso: string): string {
  const created = new Date(iso)
  const days = Math.floor((Date.now() - created.getTime()) / 86_400_000)
  if (days < 1) return 'Today'
  if (days < 2) return 'Yesterday'
  if (days < 30) return `${days.toString()}d ago`
  return created.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** An average over zero requests is not 0 ms, it is nothing to report. */
function formatLatency(row: {
  request_count_24h: number
  avg_latency_ms: number
}): string {
  if (row.request_count_24h === 0) return '—'
  return `${row.avg_latency_ms.toLocaleString()} ms`
}

interface ModelsTableProps {
  models: ModelTableRow[]
  /** Catalog slugs called without an alias; rendered after the team's own. */
  publicModels?: PublicModelUsageRow[]
  /** False while the gateway health call is still in flight. */
  healthLoaded?: boolean
  traceLogging?: TraceLoggingColumn
  latencyAlerts?: LatencyAlertsColumn
}

export function ModelsTable({
  models,
  publicModels = [],
  healthLoaded = true,
  traceLogging,
  latencyAlerts,
}: ModelsTableProps): React.JSX.Element {
  function latencyCell(model: ModelTableRow | undefined): React.JSX.Element | null {
    if (!latencyAlerts) return null
    return (
      <TableCell className="hidden md:table-cell">
        {model?.latency_alerts_enabled === undefined ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          <Switch
            aria-label={`Latency alerts for ${model.display_name}`}
            checked={model.latency_alerts_enabled}
            disabled={latencyAlerts.pending}
            onCheckedChange={(checked) => {
              latencyAlerts.onToggle(model.id, checked)
            }}
          />
        )}
      </TableCell>
    )
  }

  function traceCell(
    gatewayModelId: string | undefined,
    label: string,
  ): React.JSX.Element | null {
    if (!traceLogging) return null
    return (
      <TableCell className="hidden md:table-cell">
        {gatewayModelId === undefined ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          <Switch
            aria-label={`Trace logging for ${label}`}
            checked={traceLogging.isEnabled(gatewayModelId)}
            disabled={traceLogging.pending}
            onCheckedChange={(checked) => {
              traceLogging.onToggle(gatewayModelId, checked)
            }}
          />
        )}
      </TableCell>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Display name</TableHead>
          <TableHead>Slug</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="hidden text-right lg:table-cell">
            Requests 24h
          </TableHead>
          <TableHead className="hidden text-right lg:table-cell">Tokens</TableHead>
          <TableHead className="hidden text-right md:table-cell">
            Avg latency
          </TableHead>
          <TableHead className="hidden text-right md:table-cell">Created</TableHead>
          {traceLogging && (
            <TableHead className="hidden md:table-cell">Trace logging</TableHead>
          )}
          {latencyAlerts && (
            <TableHead className="hidden md:table-cell">Latency alerts</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {models.map((model) => (
          <TableRow key={model.id}>
            <TableCell className="font-medium">
              <div className="flex items-center gap-2">
                <Link
                  to={`/models/${model.id}`}
                  className="text-foreground hover:underline"
                >
                  {model.display_name}
                </Link>
                {model.is_default === true && <DefaultModelBadge />}
              </div>
            </TableCell>
            <TableCell className="font-mono text-xs text-muted-foreground">
              <div>{model.slug}</div>
              {model.aliases.length > 0 && (
                <div className="text-[10px] text-muted-foreground/70">
                  {model.aliases.join(', ')}
                </div>
              )}
            </TableCell>
            <TableCell>
              {healthLoaded ? (
                <ServingBadge status={model.status} />
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="hidden text-right font-mono text-xs tabular-nums lg:table-cell">
              {healthLoaded ? formatCompact(model.request_count_24h) : '—'}
            </TableCell>
            <TableCell className="hidden text-right font-mono text-xs tabular-nums lg:table-cell">
              {healthLoaded ? formatCompact(model.total_tokens) : '—'}
            </TableCell>
            <TableCell className="hidden text-right font-mono text-xs tabular-nums md:table-cell">
              {/* No requests means no latency SAMPLE. The gateway sends 0 for
                  an untrafficked model, and rendering "0 ms" reports
                  impossible performance for something that served nothing. */}
              {healthLoaded ? formatLatency(model) : '—'}
            </TableCell>
            <TableCell className="hidden text-right text-sm text-muted-foreground md:table-cell">
              {formatRelative(model.created_at)}
            </TableCell>
            {traceCell(model.gateway_model_id, model.display_name)}
            {latencyCell(model)}
          </TableRow>
        ))}

        {publicModels.map((model) => (
          <TableRow key={`public:${model.slug}`}>
            {/* The catalog's own name ("GLM 5.2"), joined server-side. Null
                only for a slug in nobody's catalog — an alias deleted inside
                the window — where an em dash beats inventing a name. Not
                bold like a team model: there is no detail page behind it. */}
            <TableCell className="text-muted-foreground">
              {model.display_name ?? '—'}
            </TableCell>
            <TableCell className="font-mono text-xs">{model.slug}</TableCell>
            <TableCell>
              {/* The gateway's own probe, same source as a team model's —
                  never a hardcoded Live, which let this band show green
                  while the Uptime band reported the provider down. */}
              <ServingBadge status={model.status} />
            </TableCell>
            <TableCell className="hidden text-right font-mono text-xs tabular-nums lg:table-cell">
              {formatCompact(model.request_count_24h)}
            </TableCell>
            <TableCell className="hidden text-right font-mono text-xs tabular-nums lg:table-cell">
              {formatCompact(model.total_tokens)}
            </TableCell>
            <TableCell className="hidden text-right font-mono text-xs tabular-nums md:table-cell">
              {formatLatency(model)}
            </TableCell>
            <TableCell className="hidden text-right text-sm text-muted-foreground md:table-cell">
              —
            </TableCell>
            {/* A public row's slug IS its canonical gateway id. */}
            {traceCell(model.slug, model.display_name ?? model.slug)}
            {latencyCell(undefined)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
